import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// A bucket-moulded sandcastle: two plump crenellated towers straight out of a
// beach pail, a joining wall with its own mini merlons, a seashell pressed
// into the front, a starfish leaning at the base — and two tiny paper flags
// on toothpick poles. Built for a breeze: the big flag flutters gently
// ("idle") and the whole shoreline picks up in "breeze", waving both flags
// with the little pennant a beat behind. Storybook batch: plump masses,
// oversized merlons and decorations, warm saturated colours + cream trim.
//
// Frame: y = 0 is the ground (the beach), +Z is the front. Roughly 0.7 m
// tall over a 1x1 tile.

const SAND = "#eac57d";        // wet-enough-to-mould sand
const SAND_DARK = "#c99e54";   // sand shading (base pat, merlon caps)
const FLAG = "#e8544f";        // the big paper flag
const PENNANT = "#3f9be0";     // the little pennant on the second tower
const CREAM = "#f6efe3";       // toothpick poles + shell highlight
const SHELL = "#f2b9c6";       // seashell pink
const STAR = "#ff9052";        // starfish orange

// ── frame ────────────────────────────────────────────────────────────────────
const TOWER_H = 0.42;                 // bucket-mould tower height
const TX = 0.21;                      // tower centres at x = +-TX
const POLE_H = 0.22;                  // flag pole above the big tower
const FLAG_BASE: [number, number, number] = [-TX, TOWER_H + POLE_H - 0.045, 0];
const PEN_BASE: [number, number, number] = [TX, TOWER_H + 0.14, 0];

// One bucket tower: the classic upside-down pail frustum with a rim ridge and
// chunky merlons pinched around the top — plus a sprinkle of shading.
function tower(x: number): Part[] {
  const merlons: Part[] = [];
  for (let i = 0; i < 5; i++) {
    const a = (i / 5) * Math.PI * 2 + 0.3;
    merlons.push({
      geometry: new THREE.BoxGeometry(0.075, 0.075, 0.055), color: SAND,
      position: [x + Math.sin(a) * 0.105, TOWER_H + 0.03, Math.cos(a) * 0.105],
      rotation: [0, a, 0],
    });
  }
  return [
    { geometry: new THREE.CylinderGeometry(0.135, 0.175, TOWER_H, 12), color: SAND,
      position: [x, TOWER_H / 2, 0] },
    // The pail's rim ridge, moulded into the sand near the top.
    { geometry: new THREE.CylinderGeometry(0.15, 0.15, 0.035, 12), color: SAND_DARK,
      position: [x, TOWER_H - 0.05, 0] },
    ...merlons,
  ];
}

// A flag's wave: a paper rectangle swinging about its pole, with a little
// scale ripple so it reads as cloth-flutter, not a stiff door. `phase`
// offsets the beat so the pennant lags the big flag.
function waveTrack(target: string, base: [number, number, number], dur: number, swing: number, phase: number) {
  const t = (k: number) => (phase + k * dur) % dur;
  const keys = [
    { k: 0.0, r: swing, s: 1.0 },
    { k: 0.25, r: 0, s: 0.92 },
    { k: 0.5, r: -swing, s: 1.0 },
    { k: 0.75, r: 0, s: 0.92 },
    { k: 1.0, r: swing, s: 1.0 },
  ].map((p) => ({ t: t(p.k), r: p.r, s: p.s }))
    .sort((a, b) => a.t - b.t);
  return {
    target,
    pivot: base,
    rotation: keys.map((p) => ({
      t: p.t, value: [0, p.r, 0] as [number, number, number], ease: "sine" as const,
    })),
    scale: keys.map((p) => ({
      t: p.t, value: [p.s, 1, 1] as [number, number, number], ease: "sine" as const,
    })),
  };
}

export const sandcastle = defineModel({
  id: "sandcastle",
  batchId: "mess",
  name: "Sandcastle",
  tags: ["mess", "cute", "animated", "prop", "beach", "summer", "castle"],
  summary: "A bucket-moulded sandcastle with a seashell, a starfish and waving paper flags.",
  description:
    "A plump toy-like sandcastle: two bucket-moulded crenellated towers joined " +
    "by a merloned wall, sitting on a patted sand base with a pink seashell " +
    "pressed in front and an orange starfish leaning at the foot. Two tiny " +
    "paper flags top it off — the idle animation flutters the big red flag, " +
    "and the breeze animation waves both, the little blue pennant a beat " +
    "behind.",
  author: "models",
  footprint: 0.42,
  tiles: { w: 1, h: 1 },
  parts(): Part[] {
    return [
      // The patted sand base the whole castle was built on.
      { geometry: new THREE.CylinderGeometry(0.36, 0.42, 0.05, 14), color: SAND_DARK,
        position: [0, 0.025, 0] },

      // Two bucket towers and the joining wall with three mini merlons.
      ...tower(-TX), ...tower(TX),
      { geometry: new THREE.BoxGeometry(0.26, 0.24, 0.11), color: SAND,
        position: [0, 0.17, 0] },
      { geometry: new THREE.BoxGeometry(0.055, 0.05, 0.11), color: SAND,
        position: [-0.075, 0.315, 0] },
      { geometry: new THREE.BoxGeometry(0.055, 0.05, 0.11), color: SAND,
        position: [0.075, 0.315, 0] },
      // A moulded doorway arch pressed into the wall's front face.
      { geometry: new THREE.CylinderGeometry(0.045, 0.045, 0.02, 10), color: SAND_DARK,
        position: [0, 0.14, 0.05], rotation: [Math.PI / 2, 0, 0] },
      { geometry: new THREE.BoxGeometry(0.09, 0.08, 0.02), color: SAND_DARK,
        position: [0, 0.095, 0.05] },

      // The seashell: a pink scallop fanned open against the front of the base.
      { geometry: new THREE.SphereGeometry(0.075, 10, 6, 0, Math.PI * 2, 0, Math.PI / 2),
        color: SHELL, position: [0.02, 0.05, 0.3], scale: [1.1, 0.55, 1], rotation: [0.25, 0, 0] },
      { geometry: new THREE.SphereGeometry(0.028, 8, 6), color: CREAM,
        position: [0.02, 0.055, 0.235] },

      // The starfish: a plump middle with five stubby arms splayed flat on the
      // sand at the left tower's foot — ellipsoids, so it reads as a star from
      // above without any tricky cone orientation.
      { geometry: new THREE.SphereGeometry(0.042, 8, 6), color: STAR,
        position: [-0.33, 0.062, 0.26], scale: [1, 0.45, 1] },
      ...[0, 1, 2, 3, 4].map((i): Part => {
        const a = (i / 5) * Math.PI * 2 + 0.6;
        return {
          geometry: new THREE.SphereGeometry(0.03, 7, 5), color: STAR,
          position: [-0.33 + Math.sin(a) * 0.062, 0.058, 0.26 + Math.cos(a) * 0.062],
          rotation: [0, a, 0],
          scale: [0.85, 0.5, 1.9],
        };
      }),

      // The big paper flag on its toothpick pole, topping the left tower.
      { geometry: new THREE.CylinderGeometry(0.012, 0.012, POLE_H, 6), color: CREAM,
        position: [-TX, TOWER_H + POLE_H / 2, 0] },
      { geometry: new THREE.SphereGeometry(0.02, 6, 5), color: FLAG,
        position: [-TX, TOWER_H + POLE_H, 0] },
      { geometry: new THREE.BoxGeometry(0.15, 0.085, 0.012), color: FLAG,
        position: [FLAG_BASE[0] + 0.085, FLAG_BASE[1], FLAG_BASE[2]], name: "flag" },

      // The little pennant on the right tower, a beat behind in the breeze.
      { geometry: new THREE.CylinderGeometry(0.01, 0.01, 0.15, 6), color: CREAM,
        position: [TX, TOWER_H + 0.065, 0] },
      { geometry: new THREE.BoxGeometry(0.105, 0.055, 0.01), color: PENNANT,
        position: [PEN_BASE[0] + 0.06, PEN_BASE[1], PEN_BASE[2]], name: "pennant" },
    ];
  },
  animations: [
    // 1. Idle — a lazy seaside flutter: the big flag swings softly about its
    //    pole while the pennant barely stirs.
    {
      id: "idle",
      label: "Flutter",
      duration: 2.6,
      loop: true,
      tracks: [
        waveTrack("flag", FLAG_BASE, 2.6, 0.22, 0),
        waveTrack("pennant", PEN_BASE, 2.6, 0.08, 2.6 * 0.3),
      ],
    },
    // 2. Breeze — the signature move: the wind picks up and both flags wave
    //    wide, the little pennant a beat behind the big flag.
    {
      id: "breeze",
      label: "Sea breeze",
      duration: 1.8,
      loop: true,
      tracks: [
        waveTrack("flag", FLAG_BASE, 1.8, 0.5, 0),
        waveTrack("pennant", PEN_BASE, 1.8, 0.42, 1.8 * 0.35),
      ],
    },
  ],
});
